/**
 * @file visitorGeo.js
 * @description Coarse visitor location for the homepage case order: the visitor's
 *   US state, read from our own same-origin Pages Function (functions/api/geo.js),
 *   which echoes Cloudflare's IP geolocation. No third party is called, nothing is
 *   sent to analytics, and only a 2-letter state code is kept, in sessionStorage
 *   for the tab's session.
 *
 *   Never blocks and never throws: any failure (dev server with no function,
 *   timeout, non-US visitor, offline, blocked storage, HTML fallback instead of
 *   JSON) resolves to null and the caller shows plain newest-first. Failures are
 *   not cached, so a transient miss does not stick for the session; a definite
 *   answer (a US state, or "not in the US") is.
 * @module lib/visitorGeo
 */

const CACHE_KEY = "abac_visitor_state";
export const GEO_TIMEOUT_MS = 800;

let inFlight = null;

// undefined = nothing cached, null = answered "no US state", string = the state.
const readCache = () => {
  try {
    const v = window.sessionStorage.getItem(CACHE_KEY);
    if (v === null) return undefined;
    return v === "" ? null : v;
  } catch {
    return undefined;
  }
};

const writeCache = (state) => {
  try { window.sessionStorage.setItem(CACHE_KEY, state || ""); } catch { /* storage blocked */ }
};

// Resolves a state code, null (definite "no US state"), or undefined (unknown:
// failure or timeout).
async function fetchState(timeoutMs) {
  if (typeof fetch !== "function") return undefined;
  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  let timer;
  const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(undefined), timeoutMs); });
  const request = (async () => {
    const res = await fetch("/api/geo", { signal: ctrl ? ctrl.signal : undefined, credentials: "omit", cache: "no-store" });
    if (!res.ok) return undefined;
    const body = await res.json(); // throws on the SPA/HTML fallback, caught below
    if (!body || typeof body !== "object" || Array.isArray(body)) return undefined;
    const code = typeof body.regionCode === "string" ? body.regionCode.trim().toUpperCase() : "";
    if (body.country === "US" && /^[A-Z]{2}$/.test(code)) return code;
    return null;
  })().catch(() => undefined);
  try {
    return await Promise.race([request, timeout]);
  } finally {
    clearTimeout(timer);
    if (ctrl) { try { ctrl.abort(); } catch { /* already settled */ } }
  }
}

/**
 * @description The visitor's 2-letter US state code, or null when unknown.
 * @param {{timeoutMs?: number}} [opts]
 * @returns {Promise<string|null>} never rejects
 */
export function getVisitorState({ timeoutMs = GEO_TIMEOUT_MS } = {}) {
  const cached = readCache();
  if (cached !== undefined) return Promise.resolve(cached);
  if (!inFlight) {
    inFlight = fetchState(timeoutMs)
      .then((state) => {
        if (state !== undefined) writeCache(state);
        return state === undefined ? null : state;
      })
      .catch(() => null)
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}
